(ns app.domain.application.guards.related
  (:require #?(:clj [app.domain.group.config.core :as group-config])
            #?(:clj [datomic.api :as d])
            #?(:cljs [core.front.date-util :as date-util])
            [app.date :as date]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.domain.project.model :as project-model]
            [app.domain.verdict.model :as verdict-model]
            [tilakone.core :as tk])
  #?(:clj (:import [java.util Date])))

(defn- fsm->available-supplemental-permits
  [fsm]
  #?(:clj (-> fsm :request :target :entity/parent group-config/available-supplemental-permits)
     ;; As the GroupConfig entities are not available in the frontend,
     ;; we piggyback the relevant data from :workspace/get through the :view-data
     :cljs (-> fsm :view-data :available-supplemental (or #{}))))

(def related-application-permitted?
  {:name    :process.guard/related-application-permitted?
   :type    :guard
   :handler (fn [{[_ permit-type category] ::tk/guard
                  :as                      fsm}]
              (let [available (fsm->available-supplemental-permits fsm)
                    requested [permit-type category]]
                (contains? available requested)))})

(def no-pending-related-workspace?
  {:name    :process.guard/no-existing-pending-related-workspace?
   :type    :guard
   :handler (fn [{{:keys [target]}                        :request
                  [_ related-permit-type category-target] ::tk/guard}]
              (->> target
                   :entity/parent
                   :workspace/related-workspaces
                   (filter (fn [{:workspace/keys [permit-type category-group-config]}]
                             (and (= permit-type related-permit-type)
                                  (-> category-group-config :group.config/target (= category-target)))))
                   (some (complement project-model/workspace-inactive-or-in-archival?))
                   not))})

(def expiration-within-allowed-timeframe?
  {:name    :process.guard/expiration-within-allowed-timeframe?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (let [days-before      (config-model/resolve-value
                                       :group.config/licence-renewal-limit
                                       (gl-model/licence-issued-workspace target))
                    valid-to         (gl-model/get-valid-to target)
                    now              #?(:clj (Date.)
                                        :cljs (date-util/now))
                    need-to-compare? #?(:clj (some-> ^Date valid-to
                                                     (.after ^Date now))
                                        :cljs (some-> valid-to
                                                      (date-util/on-or-after? now)))]
                (if (and days-before
                         need-to-compare?)
                  (let [coerce (date/coerce-timestamp-fn {:timezone (-> target :domain/authority-group :group/tz)})]
                    (some-> valid-to
                            coerce
                            (->> (date/count-days
                                   (coerce now))
                                 (>= days-before))))
                  true)))})

(defn- get-other-workspace-entities [target workspace-ids]
  #?(:clj
     ;; in the backend, find all of the given workspaces
     (some->> workspace-ids
              seq
              (d/q '[:find [?workspace-entity ...]
                     :in $ [?ws-ids ...]
                     :where
                     [?workspace :workspace/id ?ws-ids]
                     [(datomic.api/entity $ ?workspace) ?workspace-entity]]
                   (d/entity-db target)))

     :cljs
     ;; in frontend, only check that already linked workspaces match the guard
     (->> target
          :entity/parent
          :workspace/related-workspaces
          (filter (comp (set workspace-ids) :workspace/id)))))

(defn- permit-types-for-workspaces [target workspace-ids]
  (->> (get-other-workspace-entities target workspace-ids)
       (map :workspace/permit-type)
       set))

(def related-workspace-not-supplemental?
  {:name    :process.guard/related-workspace-not-supplemental
   :type    :guard
   :handler (fn [{{:keys [target]}                  :request
                  [_ {:keys [other-workspace-ids]}] ::tk/signal}]
              (->> (permit-types-for-workspaces target other-workspace-ids)
                   (every? (complement ptc-model/additional-permit-type?))))})

(defn- has-matching-property?
  "Assert that at least one of the properties of two workspaces have a matching
  municipality and roll-number.

  Note that at the moment, all properties of a workspace belong to the same municipality,
  but this check is more relaxed."
  [workspace other-workspace]
  (->> other-workspace
       :workspace/properties
       (some (fn [property]
               (->> workspace
                    :workspace/properties
                    (some (fn [my-property]
                            (->> [property my-property]
                                 (map (juxt :property/id :property/municipality))
                                 (apply =)))))))))

(def related-workspace-property-matches?
  {:name    :process.guard/related-workspace-property-matches?
   :type    :guard
   :handler (fn [{{:keys [target]}                  :request
                  [_ {:keys [other-workspace-ids]}] ::tk/signal}]
              (let [workspace (-> target :entity/parent)]
                (->> other-workspace-ids
                     (get-other-workspace-entities target)
                     (every? (partial has-matching-property? workspace)))))})

(def existing-linked-licence?
  {:name    :process.guard/existing-linked-licence?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (or (-> target :entity/parent :workspace/permit-type (not= :permit-type/L))
                  (-> target
                      verdict-model/single-verdict
                      :licence/_verdict
                      first)))})

(def no-existing-linked-licence?
  {:name    :process.guard/no-existing-linked-licence?
   :type    :guard
   :handler (complement (:handler existing-linked-licence?))})

(def guards
  [related-application-permitted?
   no-pending-related-workspace?
   expiration-within-allowed-timeframe?
   related-workspace-not-supplemental?
   related-workspace-property-matches?
   existing-linked-licence?
   no-existing-linked-licence?])
